import * as THREE from 'three';

/** Skin image dimensions (classic Minecraft layout, regions in the top half). */
const SKIN_SIZE = 64;

const DEFAULT_SKIN_URL = `${import.meta.env.BASE_URL}skins/default.png`;

/** Pixel rectangle on the skin image: x, y, width, height. */
type Rect = readonly [number, number, number, number];

/**
 * Skin regions for one box, keyed by box face in the character's LOCAL
 * frame: the character walks towards -z, so `nz` is the front.
 * `px` is the character's left side, `nx` its right side.
 */
interface BoxRegions {
  px: Rect;
  nx: Rect;
  py: Rect;
  ny: Rect;
  pz: Rect;
  nz: Rect;
}

/** Classic Minecraft skin layout. Left limbs reuse right-limb regions mirrored. */
const REGIONS: Record<'head' | 'torso' | 'arm' | 'leg', BoxRegions> = {
  head: {
    nz: [8, 8, 8, 8],
    pz: [24, 8, 8, 8],
    nx: [0, 8, 8, 8],
    px: [16, 8, 8, 8],
    py: [8, 0, 8, 8],
    ny: [16, 0, 8, 8],
  },
  torso: {
    nz: [20, 20, 8, 12],
    pz: [32, 20, 8, 12],
    nx: [16, 20, 4, 12],
    px: [28, 20, 4, 12],
    py: [20, 16, 8, 4],
    ny: [28, 16, 8, 4],
  },
  arm: {
    nz: [44, 20, 4, 12],
    pz: [52, 20, 4, 12],
    nx: [40, 20, 4, 12],
    px: [48, 20, 4, 12],
    py: [44, 16, 4, 4],
    ny: [48, 16, 4, 4],
  },
  leg: {
    nz: [4, 20, 4, 12],
    pz: [12, 20, 4, 12],
    nx: [0, 20, 4, 12],
    px: [8, 20, 4, 12],
    py: [4, 16, 4, 4],
    ny: [8, 16, 4, 4],
  },
};

/**
 * Map each face of a BoxGeometry onto its skin region.
 * With `mirror` (left limbs) the horizontal axis is flipped and the two
 * side faces swap regions, matching how Minecraft mirrors right-limb
 * textures onto left limbs.
 */
function applySkinUVs(
  geometry: THREE.BoxGeometry,
  regions: BoxRegions,
  mirror = false,
): void {
  // BoxGeometry face order: +x, -x, +y, -y, +z, -z (4 vertices each,
  // laid out top-left, top-right, bottom-left, bottom-right).
  const order: Rect[] = mirror
    ? [regions.nx, regions.px, regions.py, regions.ny, regions.pz, regions.nz]
    : [regions.px, regions.nx, regions.py, regions.ny, regions.pz, regions.nz];

  const uv = geometry.attributes.uv as THREE.BufferAttribute;
  order.forEach(([x, y, w, h], face) => {
    let u0 = x / SKIN_SIZE;
    let u1 = (x + w) / SKIN_SIZE;
    const v0 = 1 - y / SKIN_SIZE;
    const v1 = 1 - (y + h) / SKIN_SIZE;
    if (mirror) [u0, u1] = [u1, u0];
    const i = face * 4;
    uv.setXY(i, u0, v0);
    uv.setXY(i + 1, u1, v0);
    uv.setXY(i + 2, u0, v1);
    uv.setXY(i + 3, u1, v1);
  });
  uv.needsUpdate = true;
}

function loadSkin(url: string): THREE.Texture {
  const texture = new THREE.TextureLoader().load(url);
  // Nearest filtering and no mipmaps: keeps the pixel-art look and avoids
  // color bleeding between atlas regions at a distance.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Minecraft-like blocky puppet built from boxes: head, torso, two arms,
 * two legs. All parts are textured from a single skin image
 * (`client/public/skins/default.png`, classic Minecraft layout — any
 * standard Minecraft skin works as a drop-in replacement). Limbs swing
 * while walking. The group origin is at the feet.
 */
export class Puppet {
  readonly group = new THREE.Group();
  /** Eye height above the feet, used for the first-person camera. */
  static readonly EYE_HEIGHT = 1.62;
  static readonly HEIGHT = 1.8;

  private readonly head: THREE.Mesh;
  private readonly leftArm: THREE.Group;
  private readonly rightArm: THREE.Group;
  private readonly leftLeg: THREE.Group;
  private readonly rightLeg: THREE.Group;

  private walkCycle = 0;

  constructor(skinUrl: string = DEFAULT_SKIN_URL) {
    // Proportions follow the classic 32px-tall skin model, scaled so the
    // whole character is HEIGHT meters tall (1 skin px = HEIGHT/32 m).
    const u = Puppet.HEIGHT / 32;
    const material = new THREE.MeshLambertMaterial({ map: loadSkin(skinUrl) });

    // Torso: 8x12x4 px
    const torso = this.part(8 * u, 12 * u, 4 * u, REGIONS.torso, material);
    torso.position.y = 18 * u;
    this.group.add(torso);

    // Head: 8x8x8 px, pivot at the neck
    this.head = this.part(8 * u, 8 * u, 8 * u, REGIONS.head, material);
    (this.head.geometry as THREE.BoxGeometry).translate(0, 4 * u, 0);
    this.head.position.y = 24 * u;
    this.group.add(this.head);

    // Arms: 4x12x4 px, pivot at the shoulder
    this.rightArm = this.limb(4 * u, 12 * u, 4 * u, REGIONS.arm, material, false);
    this.rightArm.position.set(-6 * u, 24 * u, 0);
    this.leftArm = this.limb(4 * u, 12 * u, 4 * u, REGIONS.arm, material, true);
    this.leftArm.position.set(6 * u, 24 * u, 0);
    this.group.add(this.leftArm, this.rightArm);

    // Legs: 4x12x4 px, pivot at the hip
    this.rightLeg = this.limb(4 * u, 12 * u, 4 * u, REGIONS.leg, material, false);
    this.rightLeg.position.set(-2 * u, 12 * u, 0);
    this.leftLeg = this.limb(4 * u, 12 * u, 4 * u, REGIONS.leg, material, true);
    this.leftLeg.position.set(2 * u, 12 * u, 0);
    this.group.add(this.leftLeg, this.rightLeg);

    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
  }

  /**
   * Animate limbs.
   * @param speed horizontal speed in m/s
   * @param grounded whether the character stands on the ground
   * @param dt frame time in seconds
   */
  update(speed: number, grounded: boolean, dt: number): void {
    if (speed > 0.1 && grounded) {
      this.walkCycle += dt * speed * 2.2;
      const swing = Math.sin(this.walkCycle) * Math.min(speed / 5.6, 1) * 0.8;
      this.leftArm.rotation.x = -swing;
      this.rightArm.rotation.x = swing;
      this.leftLeg.rotation.x = swing;
      this.rightLeg.rotation.x = -swing;
    } else {
      // Ease limbs back to the rest pose.
      const relax = Math.min(dt * 10, 1);
      for (const limb of [this.leftArm, this.rightArm, this.leftLeg, this.rightLeg]) {
        limb.rotation.x *= 1 - relax;
      }
      if (!grounded) {
        // Slightly raise arms while airborne.
        this.leftArm.rotation.x = -0.35;
        this.rightArm.rotation.x = -0.35;
      }
    }
  }

  /** Tilt the head with the camera pitch (third-person nicety). */
  setHeadPitch(pitch: number): void {
    this.head.rotation.x = THREE.MathUtils.clamp(pitch * 0.6, -0.7, 0.7);
  }

  /** Hide/show the model (hidden in first person). */
  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  /** A skinned box part. */
  private part(
    w: number,
    h: number,
    d: number,
    regions: BoxRegions,
    material: THREE.Material,
    mirror = false,
  ): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(w, h, d);
    applySkinUVs(geometry, regions, mirror);
    return new THREE.Mesh(geometry, material);
  }

  /** A skinned limb whose pivot is at its top. */
  private limb(
    w: number,
    h: number,
    d: number,
    regions: BoxRegions,
    material: THREE.Material,
    mirror: boolean,
  ): THREE.Group {
    const pivot = new THREE.Group();
    const mesh = this.part(w, h, d, regions, material, mirror);
    mesh.position.y = -h / 2;
    pivot.add(mesh);
    return pivot;
  }
}
